import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Target, Building2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAuthUser, getUserId } from "@/lib/subscription";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EnterpriseHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const user = getAuthUser();
    if (!user || user.plan !== "enterprise" || !user.is_premium) {
      setError("Enterprise subscription required");
      setIsLoading(false);
      return;
    }

    const fetchHistory = async () => {
      try {
        const userId = getUserId();
        const response = await axios.get(`${API}/api/enterprise/history/${userId}`);
        setHistory(response.data.history || []);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load history");
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" onClick={() => navigate("/enterprise")} data-testid="back-to-enterprise">
            Back to Enterprise
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/enterprise")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <span className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">
            Enterprise History
          </span>
        </div>
      </header>

      <div className="pt-24 pb-12 px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="text-center space-y-2">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground">
                Analysis History
              </h1>
              <p className="text-sm text-muted-foreground">
                Your past enterprise decision analyses
              </p>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : history.length === 0 ? (
              <div className="floating-card p-8 text-center">
                <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No analyses yet</p>
                <Button
                  variant="outline"
                  className="mt-4 rounded-full"
                  onClick={() => navigate("/enterprise")}
                  data-testid="start-first-analysis"
                >
                  Start Your First Analysis
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="history-list">
                {history.map((item, i) => (
                  <motion.div
                    key={item.session_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <button
                      onClick={() => navigate(`/enterprise/analysis/${item.session_id}`)}
                      className="w-full floating-card p-4 text-left hover:border-primary/50 transition-colors"
                      data-testid={`history-item-${i}`}
                    >
                      <p className="text-sm text-foreground font-medium line-clamp-2 mb-2">
                        {item.decision_text}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {item.priority && (
                          <span className="flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            {item.priority}
                          </span>
                        )}
                        {item.department && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {item.department}
                          </span>
                        )}
                        {item.created_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
